import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  SupplierLookupResponse,
  ContractListResponse,
  ContractResponse,
  CreateContractRequest,
  CreateProjectRequest,
  CustomerLookupResponse,
  EmployeeLookupResponse,
  MeResponse,
  MoveCategoryRequest,
  MoveCategoryResponse,
  ProjectListResponse,
  ProjectResponse,
  ProjectSummaryResponse,
  UpdateContractRequest,
  UpdateProjectRequest,
} from '@timelog/pm-shared';
import {
  PROJECT_SHIP_CODE,
  PROJECT_SHIP_NAME,
  SHIP_CONTRACT_IDS,
  createProjectShipContracts,
  createProjectShipPlan,
  withFreshNodeIds,
} from '@timelog/pm-shared';
import { useTranslation } from 'react-i18next';

import { QueryKeys } from './queryKeys';
import { useApiClient } from './useApiClient';

import { useSession } from '@/auth';

/**
 * One hook per endpoint, each owning its query key. Mirrors the Web repo's
 * `src/api/REST/` convention; when this grows past a handful of endpoints, split
 * it into a directory per endpoint.
 */

export const useMe = () => {
  const api = useApiClient();
  // The sidebar renders outside the sign-in gate; asking before there is a
  // session can only 401, and that error would sit in the cache after sign-in.
  const { isAuthenticated } = useSession();
  return useQuery({
    queryKey: QueryKeys.Me,
    queryFn: () => api.get<MeResponse>('/me'),
    enabled: isAuthenticated,
  });
};

export const useProjects = () => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.Projects,
    queryFn: () => api.get<ProjectListResponse>('/projects'),
  });
};

export const useProject = (id: string | undefined) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.Project(id ?? ''),
    queryFn: () => api.get<ProjectResponse>(`/projects/${id}`),
    enabled: Boolean(id),
  });
};

/** The derived read model — every number computed by the backend on load. */
export const useProjectSummary = (id: string | undefined) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.ProjectSummary(id ?? ''),
    queryFn: () => api.get<ProjectSummaryResponse>(`/projects/${id}/summary`),
    enabled: Boolean(id),
  });
};

export const useContracts = (projectId: string | undefined) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.ProjectContracts(projectId ?? ''),
    queryFn: () => api.get<ContractListResponse>(`/projects/${projectId}/contracts`),
    enabled: Boolean(projectId),
  });
};

/**
 * Whole-document replace (BR-20). The response carries server-assigned codes
 * (BR-6) and the bumped version — callers MUST adopt both; the autosave hook
 * owns that merge. The query cache is updated here so other readers stay fresh.
 */
export const useUpdateProject = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: UpdateProjectRequest) =>
      api.put<ProjectResponse>(`/projects/${projectId}`, request),
    onSuccess: (response) => {
      queryClient.setQueryData(QueryKeys.Project(projectId), response);
    },
  });
};

export const useCreateProject = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateProjectRequest) => api.post<ProjectResponse>('/projects', request),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.Projects }),
  });
};

/**
 * Creates the full "Project Ship" sample through the ordinary public endpoints:
 * project → three contracts (created, then their budget documents applied) →
 * the plan wired to the server-assigned contract ids.
 */
export const useCreateSampleProject = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name?: string) => {
      const projectName = name?.trim() || PROJECT_SHIP_NAME;
      const project = await api.post<ProjectResponse>('/projects', {
        name: projectName,
        code: PROJECT_SHIP_CODE,
        currency: 'DKK',
        blendedCostRate: 650,
      });

      const idByFixture: Record<string, string> = {};
      for (const fixture of createProjectShipContracts()) {
        const created = await api.post<ContractResponse>(`/projects/${project.id}/contracts`, {
          name: fixture.name,
          type: fixture.type,
        });
        await api.put<ContractResponse>(`/projects/${project.id}/contracts/${created.id}`, {
          document: fixture.document,
          status: 'active',
          version: created.version,
        });
        idByFixture[fixture.id] = created.id;
      }

      // Creating the first contract set the project default and bumped the version.
      const fresh = await api.get<ProjectResponse>(`/projects/${project.id}`);
      // Fresh node ids, not the fixture's constants: `work_items.id` is the plan node id
      // verbatim (ADR 0021), so two sample projects sharing them cannot both be projected.
      const plan = withFreshNodeIds(
        createProjectShipPlan({
          a: idByFixture[SHIP_CONTRACT_IDS.a]!,
          b: idByFixture[SHIP_CONTRACT_IDS.b]!,
          c: idByFixture[SHIP_CONTRACT_IDS.c]!,
        }),
      );

      return api.put<ProjectResponse>(`/projects/${project.id}`, {
        document: { ...plan, name: projectName },
        status: 'active',
        version: fresh.version,
      });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.Projects }),
  });
};

/** Replace one row of the cached contract list so every reader stays fresh. */
const replaceContractInList = (
  list: ContractListResponse | undefined,
  row: ContractResponse,
): ContractListResponse | undefined =>
  list && {
    contracts: list.contracts.map((contract) => (contract.id === row.id ? row : contract)),
  };

/**
 * Creating a contract can also mutate the project (the first contract becomes
 * the default, BR-4) — both caches invalidate.
 */
export const useCreateContract = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (request: CreateContractRequest) =>
      api.post<ContractResponse>(`/projects/${projectId}/contracts`, request),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectContracts(projectId) });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.Project(projectId) });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectSummary(projectId) });
    },
  });
};

/**
 * Whole-document replace (BR-20). The response carries the bumped version —
 * the contract autosave hook owns adopting it. `contractId` is a variable, not
 * a hook parameter, so one mutation serves whichever contract is active.
 */
export const useUpdateContract = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contractId, request }: { contractId: string; request: UpdateContractRequest }) =>
      api.put<ContractResponse>(`/projects/${projectId}/contracts/${contractId}`, request),
    onSuccess: (response) => {
      queryClient.setQueryData(QueryKeys.ProjectContracts(projectId), (list) =>
        replaceContractInList(list as ContractListResponse | undefined, response),
      );
    },
  });
};

/**
 * BR-7: deleting a referenced contract 409s unless `reassignTo` names a
 * sibling — the caller reads the count from the ApiError message and reissues.
 */
export const useDeleteContract = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contractId, reassignTo }: { contractId: string; reassignTo?: string }) =>
      api.delete(
        `/projects/${projectId}/contracts/${contractId}` +
          (reassignTo ? `?reassignTo=${reassignTo}` : ''),
      ),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectContracts(projectId) });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.Project(projectId) });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectSummary(projectId) });
    },
  });
};

/**
 * Duplicate = POST + PUT of the copied document (no dedicated endpoint;
 * non-atomic by design — a failure between the two leaves an empty copy).
 * `billedVia` marks are stripped: the copy never took part in the source's
 * BR-10 moves, so carrying them over would claim target-side lines that were
 * never added.
 */
export const useDuplicateContract = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  // The copy's name is stored, so it is written in the user's language (ADR 0049).
  const { t } = useTranslation('pmFinancials');
  return useMutation({
    mutationFn: async (source: ContractResponse) => {
      const created = await api.post<ContractResponse>(`/projects/${projectId}/contracts`, {
        name: t('contractCopy.Name', { name: source.name }),
        type: source.type,
      } satisfies CreateContractRequest);

      const document = {
        ...source.document,
        categories: source.document.categories.map(
          ({ billedVia: _stripped, ...category }) => category,
        ),
      };
      return api.put<ContractResponse>(`/projects/${projectId}/contracts/${created.id}`, {
        document,
        status: source.status,
        version: created.version,
      } satisfies UpdateContractRequest);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectContracts(projectId) });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectSummary(projectId) });
    },
  });
};

/** BR-10: one transactional write over both contracts (ADR 0011). */
export const useMoveCategory = (projectId: string) => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contractId, request }: { contractId: string; request: MoveCategoryRequest }) =>
      api.post<MoveCategoryResponse>(
        `/projects/${projectId}/contracts/${contractId}/move-category`,
        request,
      ),
    onSuccess: (response) => {
      queryClient.setQueryData(QueryKeys.ProjectContracts(projectId), (list) => {
        const once = replaceContractInList(
          list as ContractListResponse | undefined,
          response.source,
        );
        return replaceContractInList(once, response.target);
      });
      void queryClient.invalidateQueries({ queryKey: QueryKeys.ProjectSummary(projectId) });
    },
  });
};

/**
 * TimeLog lookups (server-side proxy, ADR 0008). Gate `enabled` on
 * useMe().data.features.lookups !== 'off' so disabled deployments never
 * probe; upstream failures surface as query errors and the pickers degrade.
 */
export const useEmployeeLookup = (q: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.LookupEmployees(q),
    queryFn: () => api.get<EmployeeLookupResponse>(`/lookup/employees?q=${encodeURIComponent(q)}`),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
};

export const useSupplierLookup = (q: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.LookupSuppliers(q),
    queryFn: () => api.get<SupplierLookupResponse>(`/lookup/suppliers?q=${encodeURIComponent(q)}`),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60_000,
    retry: false,
  });
};

export const useCustomerLookup = (q: string, enabled = true) => {
  const api = useApiClient();
  return useQuery({
    queryKey: QueryKeys.LookupCustomers(q),
    queryFn: () => api.get<CustomerLookupResponse>(`/lookup/customers?q=${encodeURIComponent(q)}`),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: 60_000,
    retry: false,
  });
};

export const useDeleteProject = () => {
  const api = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/projects/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: QueryKeys.Projects }),
  });
};
